import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/shared/hooks/useUser";
import { customAttributesService } from "@/shared/services/askedAttributes.service";
import { getCustomAttributeValue } from "@/shared/types/";
import { CustomAttributesResponse } from "@/shared/types/askedAttributes.types";

const FIELD_LABELS: Record<string, string> = {
  address_line1: "Adresse",
  address_line2: "Complément d'adresse",
  zip_code: "Code postal",
  city: "Ville",
  organization_site_id: "Site",
};

const labelFor = (field: string) => FIELD_LABELS[field] ?? field;

export const useUserMandatoryFieldsCheck = () => {
  const userData = useUser();

  const { data, isLoading } = useQuery<CustomAttributesResponse, Error>({
    queryKey: ["userMandatoryFields"],
    queryFn: () => customAttributesService.getAskedAttributes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled: !!userData.id,
  });

  const missingAttributes = data?.custom_attributes ?? [];
  const missingMandatoryFieldNames = data?.missing_mandatory_fields ?? [];

  // Filtrer les champs vraiment manquants en croisant avec les données utilisateur
  // WORKAROUND: L'endpoint /asked_attributes a un bug côté backend pour les attributs custom_text
  // qui ne sont pas correctement filtrés même quand ils sont déjà remplis.
  // Cette logique compense temporairement ce bug en vérifiant manuellement les valeurs.
  const mandatoryMissingFields = useMemo<{ name: string }[]>(() => {
    const customAttributeMissing = missingAttributes
      .filter((attr) => {
        if (!attr.employee_mandatory) return false;

        const existingValue = userData.custom_attributes?.find(
          (userAttr) => userAttr.attribute_id === attr.id,
        );

        if (existingValue && getCustomAttributeValue(existingValue).trim()) {
          return false;
        }

        return true;
      })
      .map((attr) => ({ name: attr.name }));

    const builtInMissing = missingMandatoryFieldNames.map((field) => ({ name: labelFor(field) }));

    return [...builtInMissing, ...customAttributeMissing];
  }, [missingAttributes, missingMandatoryFieldNames, userData.custom_attributes]);

  const hasMandatoryMissingFields = mandatoryMissingFields.length > 0;

  // Pas de re-dérivation côté front : le serveur ne liste que les formulaires
  // qui bloquent encore le salarié.
  const mandatoryCustomForms = data?.mandatory_custom_forms ?? [];

  return {
    hasMandatoryMissingFields,
    mandatoryMissingFields,
    missingMandatoryFieldKeys: missingMandatoryFieldNames,
    mandatoryCustomForms,
    pendingMandatoryCustomForm: mandatoryCustomForms[0] ?? null,
    isLoading,
  };
};
